/*
  Devil Daves - Deal Selector controller
  Drives <dd-deal-selector>: writes variant id + selling_plan to the
  product form, updates prices, swaps the gallery image, renders per-cell
  strike-through + green subscription prices, and the subscribe box was/now.
*/
(function () {
  if (customElements.get('dd-deal-selector')) return;

  class DdDealSelector extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.formId = this.dataset.formId;
      this.form = document.getElementById(this.formId);
      this.idInput = this.form ? this.form.querySelector('input[name="id"], .product-variant-id') : null;

      try {
        const dataEl = this.querySelector('[data-dd-variants]');
        this.variants = dataEl ? JSON.parse(dataEl.textContent) : [];
      } catch (e) {
        this.variants = [];
      }

      this.quantityRadios = Array.from(this.querySelectorAll('.dd-deal__radio'));
      this.purchaseRadios = Array.from(this.querySelectorAll('.dd-deal__purchase-radio'));
      this.planSelect = this.querySelector('[data-dd-plan-select]');
      this.priceContainer = document.getElementById('price-' + this.sectionId);

      this.bindEvents();

      this.selectedVariantId = this.getCheckedValue(this.quantityRadios);
      this.syncPurchaseMode();
      this.updateVariant(this.selectedVariantId, false);
      // ensure subscribe box shows the discount on first paint regardless of mode
      this.refreshSubBox();
      this.updateCellPrices();
      this.updateSavingsPoint();
    }

    bindEvents() {
      this.quantityRadios.forEach((r) =>
        r.addEventListener('change', () => {
          this.markSelected(r, '.dd-deal__cell');
          this.updateVariant(r.value, true);
        })
      );
      this.purchaseRadios.forEach((r) =>
        r.addEventListener('change', () => {
          this.markSelected(r, '.dd-deal__purchase');
          this.syncPurchaseMode();
        })
      );
      if (this.planSelect) {
        this.planSelect.addEventListener('change', () => {
          this.applySellingPlan();
          this.refreshSubBox();
          this.updateCellPrices();
          this.updateSavingsPoint();
        });
      }
    }

    getCheckedValue(radios) {
      const checked = radios.find((r) => r.checked);
      return checked ? checked.value : (radios[0] ? radios[0].value : null);
    }

    getPlanId() {
      return this.planSelect ? this.planSelect.value : null;
    }

    markSelected(input, selectorClass) {
      const groupName = input.name;
      this.querySelectorAll('input[name="' + groupName + '"]').forEach((r) => {
        const label = r.closest(selectorClass);
        if (label) label.classList.toggle('is-selected', r.checked);
      });
    }

    getVariant(id) {
      return this.variants.find((v) => String(v.id) === String(id));
    }

    /* ---- Variant change ---- */
    updateVariant(variantId, doImageSwap) {
      if (!variantId) return;
      this.selectedVariantId = variantId;
      const variant = this.getVariant(variantId);

      if (this.idInput) {
        this.idInput.value = variantId;
        this.idInput.removeAttribute('disabled');
      }

      if (variant) {
        const otPrice = this.querySelector('[data-dd-onetime-price]');
        if (otPrice) otPrice.textContent = this.formatMoney(variant.price);
      }

      this.refreshSectionPrice(variantId);

      if (doImageSwap && variant && variant.featured_media) {
        this.swapMedia(variant.featured_media.id);
      }

      this.applySellingPlan();
      this.refreshSubBox();
      this.updateCellPrices();
      this.updateUrl(variantId);
    }

    /* ---- One-time vs Subscribe ---- */
    syncPurchaseMode() {
      const mode = this.getCheckedValue(this.purchaseRadios) || 'onetime';
      const subBlocks = Array.from(this.querySelectorAll('.dd-deal__purchase--sub'));

      subBlocks.forEach((block) => {
        const freq = block.querySelector('[data-dd-frequencies]');
        if (freq) freq.classList.toggle('is-active', mode === 'subscription');
        const points = block.querySelector('.dd-deal__points');
        if (points) points.classList.toggle('is-active', mode === 'subscription');
      });

      this.classList.toggle('is-subscription', mode === 'subscription');

      if (mode === 'subscription') {
        this.applySellingPlan();
      } else {
        this.clearSellingPlan();
        if (this.selectedVariantId) this.refreshSectionPrice(this.selectedVariantId);
      }
      // subscribe box always shows the discounted was/now; cell strike only in sub mode
      this.refreshSubBox();
      this.updateCellPrices();
    }

    /* ---- Subscribe box always shows discounted was/now for current variant+plan ---- */
    refreshSubBox() {
      const wasEl = this.querySelector('[data-dd-sub-was]');
      const nowEl = this.querySelector('[data-dd-sub-now]');
      const variant = this.getVariant(this.selectedVariantId);
      if (!variant) return;

      const planId = this.getPlanId();
      const subPrice = planId ? this.getPlanPrice(this.selectedVariantId, planId) : null;

      if (subPrice != null) {
        if (wasEl) wasEl.textContent = this.formatMoney(variant.price);
        if (nowEl) nowEl.textContent = this.formatMoney(subPrice);
      } else {
        // no plan price available - just show the variant price, no strike
        if (wasEl) wasEl.textContent = '';
        if (nowEl) nowEl.textContent = this.formatMoney(variant.price);
      }
    }

    /* ---- First selling point reflects the selected plan's real discount % ---- */
    updateSavingsPoint() {
      const el = this.querySelector('[data-dd-savings-point]');
      if (!el || !this.planSelect) return;
      const opt = this.planSelect.options[this.planSelect.selectedIndex];
      if (!opt) return;
      const pct = parseFloat(opt.getAttribute('data-plan-discount'));
      if (isNaN(pct) || pct <= 0) return;
      const tmpl = el.getAttribute('data-dd-savings-template') || 'Save [discount] on Every Order';
      el.textContent = tmpl.replace('[discount]', pct + '%');
    }

    /* ---- Per-cell strike-through + green subscription price (only in sub mode) ---- */
    updateCellPrices() {
      const mode = this.getCheckedValue(this.purchaseRadios) || 'onetime';
      const planId = this.getPlanId();
      const cells = Array.from(this.querySelectorAll('.dd-deal__cell'));

      cells.forEach((cell) => {
        const variantId = cell.dataset.variantId;
        const subEl = cell.querySelector('[data-dd-cell-sub]');
        if (!subEl) return;

        if (mode === 'subscription' && planId) {
          const subPrice = this.getPlanPrice(variantId, planId);
          if (subPrice != null) {
            subEl.textContent = this.formatMoney(subPrice);
            cell.classList.add('is-sub-priced');
            return;
          }
        }
        subEl.textContent = '';
        cell.classList.remove('is-sub-priced');
      });
    }

    /* ---- Selling plan handling ---- */
    applySellingPlan() {
      const mode = this.getCheckedValue(this.purchaseRadios) || 'onetime';
      if (mode !== 'subscription') {
        this.clearSellingPlan();
        return;
      }
      const planId = this.getPlanId();
      if (!planId) {
        this.clearSellingPlan();
        return;
      }
      this.setSellingPlanInput(planId);

      const planPrice = this.getPlanPrice(this.selectedVariantId, planId);
      if (planPrice != null) {
        this.setBigPrice(this.formatMoney(planPrice));
      }
    }

    getPlanPrice(variantId, planId) {
      const variant = this.getVariant(variantId);
      if (!variant || !variant.selling_plan_allocations) return null;
      const alloc = variant.selling_plan_allocations.find(
        (a) => String(a.selling_plan_id) === String(planId)
      );
      return alloc ? alloc.price : null;
    }

    setSellingPlanInput(planId) {
      if (!this.form) return;
      let input = this.form.querySelector('input[name="selling_plan"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'selling_plan';
        this.form.appendChild(input);
      }
      input.value = planId;
    }

    clearSellingPlan() {
      if (!this.form) return;
      const input = this.form.querySelector('input[name="selling_plan"]');
      if (input) input.value = '';
    }

    /* ---- Price rendering ---- */
    refreshSectionPrice(variantId) {
      if (!this.priceContainer) return;
      const url = this.dataset.url + '?variant=' + encodeURIComponent(variantId) + '&section_id=' + this.sectionId;
      fetch(url)
        .then((res) => res.text())
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const newPrice = html.getElementById('price-' + this.sectionId);
          if (newPrice && this.priceContainer) {
            this.priceContainer.innerHTML = newPrice.innerHTML;
          }
        })
        .catch(() => {});
    }

    setBigPrice(text) {
      if (!this.priceContainer) return;
      const regular = this.priceContainer.querySelector('.price-item--regular');
      if (regular) regular.textContent = text;
    }

    /* ---- Gallery image swap ---- */
    swapMedia(mediaId) {
      const gallery =
        document.querySelector('media-gallery[id^="MediaGallery-' + this.sectionId + '"]') ||
        document.getElementById('MediaGallery-' + this.sectionId);

      if (gallery && typeof gallery.setActiveMedia === 'function') {
        const mediaEl =
          gallery.querySelector('[data-media-id="' + this.sectionId + '-' + mediaId + '"]') ||
          gallery.querySelector('[data-media-id$="-' + mediaId + '"]') ||
          gallery.querySelector('[data-media-id="' + mediaId + '"]');
        if (mediaEl && mediaEl.dataset.mediaId) {
          gallery.setActiveMedia(mediaEl.dataset.mediaId, true);
          return;
        }
      }

      const thumb =
        document.querySelector('[data-target$="-' + mediaId + '"]') ||
        document.querySelector('button[aria-label*="' + mediaId + '"]');
      if (thumb) thumb.click();
    }

    /* ---- URL ---- */
    updateUrl(variantId) {
      if (this.dataset.updateUrl === 'false') return;
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url);
      } catch (e) {}
    }

    /* ---- Money formatting ---- */
    formatMoney(cents) {
      const fmt = (window.Shopify && window.Shopify.money_format) || '${{amount}}';
      const amount = (cents / 100).toFixed(2);
      return fmt.replace(/\{\{\s*amount\s*\}\}/, amount);
    }
  }

  customElements.define('dd-deal-selector', DdDealSelector);
})();
